export class ApiError extends Error {
    constructor(
        readonly status: number,
        readonly url: string,
    ) {
        super(`Request to ${url} failed with status ${status}`);
        this.name = "ApiError";
    }
}

/** Параметры, которые уходят в query string или в form-urlencoded тело */
export type RequestParams = Record<string, string | number>;

function toSearchParams(params: RequestParams): URLSearchParams {
    return new URLSearchParams(Object.entries(params).map(([key, value]) => [key, String(value)]));
}

/** Добавить параметры к URL в query string */
export function withQuery(url: string, params: RequestParams): string {
    return `${url}?${toSearchParams(params)}`;
}

/** Заголовки как у axios-клиента в v2: он слал токен Space во все API */
async function request<TResponse>(url: string, init: RequestInit): Promise<TResponse> {
    const response = await fetch(url, {
        ...init,
        headers: {
            Authorization: `Bearer ${import.meta.env.VITE_SPACE_API_TOKEN}`,
            "X-Requested-With": "XMLHttpRequest",
            ...init.headers,
        },
    });

    if (!response.ok) {
        throw new ApiError(response.status, url);
    }

    return response.json() as Promise<TResponse>;
}

/** POST с JSON-телом */
export function postJson<TResponse>(url: string, body: unknown): Promise<TResponse> {
    return request<TResponse>(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
}

/** POST с form-urlencoded телом */
export function postForm<TResponse>(url: string, body: RequestParams = {}): Promise<TResponse> {
    return request<TResponse>(url, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: toSearchParams(body),
    });
}

/** POST с multipart/form-data телом. Content-Type с boundary браузер ставит сам */
export function postFormData<TResponse>(url: string, body: FormData): Promise<TResponse> {
    return request<TResponse>(url, { method: "POST", body });
}

/** GET с параметрами в query string */
export function getJson<TResponse>(url: string, params: RequestParams): Promise<TResponse> {
    return request<TResponse>(withQuery(url, params), { method: "GET" });
}
